import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { clearClientCaches } from "@/lib/privacy-cache";
import { privacySignature, setPrivacySession } from "@/lib/privacy-session";
import { hasClientPrivacy } from "@/lib/client-privacy";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [loading, setLoading] = useState(true);
  const currentUser = useRef(null);
  const refreshInFlight = useRef(false);

  async function applyUser(data) {
    const signature = privacySignature(data);
    if (localStorage.getItem("fb_cache_identity") !== signature) {
      setPrivacySession(null);
      await clearClientCaches();
      localStorage.setItem("fb_cache_identity", signature);
    }
    currentUser.current = data;
    setPrivacySession(data);
    setUser(data);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled || currentUser.current) return;
        await applyUser(data);
      } catch (e) {
        if (cancelled || currentUser.current) return;
        setUser(false);
        setPrivacySession(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const refresh = async () => {
      if (!currentUser.current || currentUser.current.user_type === "client" || refreshInFlight.current) return;
      refreshInFlight.current = true;
      try {
        const { data } = await api.get("/auth/me");
        await applyUser(data);
      } catch (err) {
        if (!hasClientPrivacy(currentUser.current) && ![401, 403].includes(err?.response?.status)) return;
        // Fail closed when current permissions cannot be confirmed.
        currentUser.current = null;
        setPrivacySession(null);
        setUser(false);
      } finally {
        refreshInFlight.current = false;
      }
    };
    const interval = setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    window.addEventListener("fb-client-privacy-changed", refresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("fb-client-privacy-changed", refresh);
    };
  }, []);

  async function loginStaff(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("fb_token", data.token);
    await applyUser({ ...data, user_type: "staff" });
    return data;
  }

  async function loginWithData(data) {
    if (data.token) localStorage.setItem("fb_token", data.token);
    await applyUser({ ...data, user_type: "staff" });
  }

  async function verifyMagicLink(token) {
    const { data } = await api.post("/auth/client/verify-link", { token });
    if (data.purpose === "setup") {
      return data;
    }
    if (data.token) localStorage.setItem("fb_token", data.token);
    await applyUser({ ...data, user_type: "client" });
    return data;
  }

  async function completeSetup(payload) {
    const { data } = await api.post("/auth/client/complete-setup", payload);
    if (data.token) localStorage.setItem("fb_token", data.token);
    await applyUser({ ...data, user_type: "client" });
    return data;
  }

  async function logout() {
    currentUser.current = null;
    setPrivacySession(null);
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("fb_token");
    try {
      await clearClientCaches();
      localStorage.removeItem("fb_cache_identity");
    } finally {
      setUser(false);
    }
  }

  function updateUser(partial) {
    setUser((prev) => prev ? { ...prev, ...partial } : prev);
  }

  return (
    <AuthContext.Provider key={privacySignature(user)} value={{ user, loading, loginStaff, loginWithData, verifyMagicLink, completeSetup, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { formatApiError };
